import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('polaris_token');
    const savedUser = localStorage.getItem('polaris_user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    localStorage.setItem('polaris_token', data.token);
    localStorage.setItem('polaris_user', JSON.stringify(data.user));
    setUser(data.user);

    return data.user;
  };

  const register = async (username, email, password, registrationCode) => {
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password, registrationCode })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Registration failed');
    }

    localStorage.setItem('polaris_token', data.token);
    localStorage.setItem('polaris_user', JSON.stringify(data.user));
    setUser(data.user);

    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('polaris_token');
    localStorage.removeItem('polaris_user');
    setUser(null);
  };

  const getToken = () => localStorage.getItem('polaris_token');

  const isAdmin = () => user?.role === 'admin';
  const isManager = () => user?.role === 'manager';
  const isTech = () => user?.role === 'tech';
  const isManagerOrAdmin = () => ['admin', 'manager'].includes(user?.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, getToken, isAdmin, isManager, isTech, isManagerOrAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};
